import { useCallback, useEffect, useMemo, useState } from "react";
import {
  defaultViews,
  loadCustomViews,
  saveCustomViews,
  type SavedView,
} from "./issueViewModel";

/**
 * Controller for the project board's saved views: the built-in presets plus the user's custom views,
 * persisted per (project, scope) in localStorage. Persistence I/O lives here (not in the view) and the
 * custom set is (re)loaded whenever the stable storage key changes — the key must derive from a stable
 * projectId, never from the async-loaded issue list, or saves and loads target different keys.
 */
export function useSavedViews(projectId: string | null, scopeKey: string, currentUserId?: string) {
  const storageKey = `${projectId ?? "p"}:${scopeKey}`;

  const builtins = useMemo(() => {
    const v = defaultViews(currentUserId ?? null);
    return currentUserId ? v : v.filter((x) => x.id !== "mine");
  }, [currentUserId]);

  const [custom, setCustom] = useState<SavedView[]>([]);
  // Reload custom views whenever the (stable) key changes — not once in a useState initializer.
  useEffect(() => {
    setCustom(loadCustomViews(storageKey));
  }, [storageKey]);

  const views = useMemo(() => [...builtins, ...custom], [builtins, custom]);

  const addView = useCallback((v: SavedView) => {
    setCustom((cur) => {
      const next = [...cur, v];
      saveCustomViews(storageKey, next);
      return next;
    });
  }, [storageKey]);

  const removeView = useCallback((id: string) => {
    setCustom((cur) => {
      const next = cur.filter((x) => x.id !== id);
      saveCustomViews(storageKey, next);
      return next;
    });
  }, [storageKey]);

  return { views, builtins, addView, removeView };
}
